import React, { useId } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { btnSecondaryClass, formControlClass } from "../../../dashboardStyles";
import { Field, PanelNote, labelTextClass } from "../siteFields";
import { ABOUT_HTML_MARKER, ABOUT_ICONS, safeAboutUrl } from "../../../../../utils/aboutContent";
import { ICON_NAMES } from "../../../../../utils/aboutBlocks";
import AboutContent from "../../../../about-content/AboutContent";
import RichTextField from "./RichTextField";
import AboutImageField from "./AboutImageField";

/**
 * One form per kind of About block. Each edits a plain block object
 * (utils/aboutBlocks.js) through `update(patch)`; writing it back out as HTML
 * is the editor's job, not the form's.
 */

const k = "Dashboard.AboutEditor";
const MAX_CARDS = 6;
const MAX_QUESTIONS = 20;

// Complete classes, not one plus an override: two hover colours would tie.
const iconButtonBase =
  "inline-flex size-11 cursor-pointer items-center justify-center rounded-lg text-ink-500 transition hover:bg-ink-100 focus-visible:outline-2 focus-visible:outline-brand-600 disabled:cursor-not-allowed disabled:opacity-40";
const iconButtonClass = `${iconButtonBase} hover:text-ink-900`;
const removeButtonClass = `${iconButtonBase} hover:text-red-600`;

/** Mutually exclusive options as radios, each a full-height target. */
const RadioRow = ({ name, label, value, options, onChange }) => (
  <fieldset className="m-0 min-w-0 border-0 p-0">
    <legend className={`${labelTextClass} mb-1 p-0`}>{label}</legend>
    <div className="flex flex-wrap gap-x-6">
      {options.map((option) => (
        <label
          key={option.value}
          className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg text-sm text-ink-800 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-600"
        >
          <input
            type="radio"
            name={name}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            className="sr-only"
          />
          <span
            className={`flex size-5 items-center justify-center rounded-full border-2 ${
              value === option.value ? "border-brand-600" : "border-ink-300"
            }`}
            aria-hidden="true"
          >
            {value === option.value && <span className="size-2.5 rounded-full bg-brand-600" />}
          </span>
          {option.label}
        </label>
      ))}
    </div>
  </fieldset>
);

RadioRow.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.string, label: PropTypes.string })).isRequired,
  onChange: PropTypes.func.isRequired,
};

/** One item of a list (a card, a question): its name, and moving or removing it. */
const ListItem = ({ title, index, count, onMove, onRemove, removeLabel, children }) => {
  const { t } = useTranslation();
  const titleId = useId();
  return (
    <div role="group" aria-labelledby={titleId} className="flex min-w-0 flex-col gap-3 rounded-xl border border-ink-200 bg-ink-50/60 p-3 sm:p-4">
      <div className="flex items-center justify-between gap-2">
        <span id={titleId} className="text-sm font-semibold text-ink-900">{title}</span>
        <span className="flex">
          <button type="button" className={iconButtonClass} disabled={index === 0} onClick={() => onMove(-1)} aria-label={t(`${k}.moveUp`)} title={t(`${k}.moveUp`)}>
            <i className="bi bi-arrow-up" aria-hidden="true" />
          </button>
          <button type="button" className={iconButtonClass} disabled={index === count - 1} onClick={() => onMove(1)} aria-label={t(`${k}.moveDown`)} title={t(`${k}.moveDown`)}>
            <i className="bi bi-arrow-down" aria-hidden="true" />
          </button>
          <button type="button" className={removeButtonClass} disabled={count === 1} onClick={onRemove} aria-label={removeLabel} title={removeLabel}>
            <i className="bi bi-trash3" aria-hidden="true" />
          </button>
        </span>
      </div>
      {children}
    </div>
  );
};

ListItem.propTypes = {
  title: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  count: PropTypes.number.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const moveItem = (list, index, step) => {
  const next = [...list];
  const [item] = next.splice(index, 1);
  next.splice(index + step, 0, item);
  return next;
};

const formPropTypes = {
  block: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const TitleField = ({ block, update, label }) => (
  <Field id={`${block.id}-title`} label={label}>
    <input
      id={`${block.id}-title`}
      type="text"
      maxLength={200}
      value={block.title}
      onChange={(event) => update({ title: event.target.value })}
      className={formControlClass}
    />
  </Field>
);

TitleField.propTypes = { block: PropTypes.object.isRequired, update: PropTypes.func.isRequired, label: PropTypes.string.isRequired };

const TextForm = ({ block, update, disabled }) => {
  const { t } = useTranslation();
  return (
    <>
      <TitleField block={block} update={update} label={t(`${k}.fields.title`)} />
      <RichTextField
        id={`${block.id}-body`}
        label={t(`${k}.fields.content`)}
        value={block.body}
        headings
        disabled={disabled}
        onChange={(body) => update({ body })}
      />
      <RadioRow
        name={`${block.id}-align`}
        label={t(`${k}.fields.align`)}
        value={block.align}
        onChange={(align) => update({ align })}
        options={[
          { value: "left", label: t(`${k}.fields.alignLeft`) },
          { value: "center", label: t(`${k}.fields.alignCenter`) },
        ]}
      />
    </>
  );
};

const CardsForm = ({ block, update }) => {
  const { t } = useTranslation();
  const setCard = (index, patch) =>
    update({ cards: block.cards.map((card, i) => (i === index ? { ...card, ...patch } : card)) });

  return (
    <>
      <TitleField block={block} update={update} label={t(`${k}.fields.sectionTitle`)} />
      <Field id={`${block.id}-intro`} label={t(`${k}.fields.intro`)} optional>
        <textarea
          id={`${block.id}-intro`}
          rows={2}
          maxLength={500}
          value={block.intro}
          onChange={(event) => update({ intro: event.target.value })}
          className={`${formControlClass} resize-y`}
        />
      </Field>
      <div className="flex flex-col gap-3">
        {block.cards.map((card, index) => (
          <ListItem
            key={index}
            title={t(`${k}.fields.card`, { number: index + 1 })}
            index={index}
            count={block.cards.length}
            removeLabel={t(`${k}.fields.removeCard`)}
            onMove={(step) => update({ cards: moveItem(block.cards, index, step) })}
            onRemove={() => update({ cards: block.cards.filter((_, i) => i !== index) })}
          >
            <div className="flex items-end gap-2">
              <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700" aria-hidden="true">
                {ABOUT_ICONS[card.icon] ? <i className={`bi ${ABOUT_ICONS[card.icon]} text-lg`} /> : <i className="bi bi-dash text-ink-400" />}
              </span>
              <div className="min-w-0 flex-1">
                <Field id={`${block.id}-card-${index}-icon`} label={t(`${k}.fields.icon`)}>
                  <select
                    id={`${block.id}-card-${index}-icon`}
                    value={card.icon}
                    onChange={(event) => setCard(index, { icon: event.target.value })}
                    className={formControlClass}
                  >
                    <option value="">{t(`${k}.icons.none`)}</option>
                    {ICON_NAMES.map((name) => (
                      <option key={name} value={name}>
                        {t(`${k}.icons.${name}`)}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
            <Field id={`${block.id}-card-${index}-title`} label={t(`${k}.fields.title`)}>
              <input
                id={`${block.id}-card-${index}-title`}
                type="text"
                maxLength={120}
                value={card.title}
                onChange={(event) => setCard(index, { title: event.target.value })}
                className={formControlClass}
              />
            </Field>
            <Field id={`${block.id}-card-${index}-text`} label={t(`${k}.fields.description`)}>
              <textarea
                id={`${block.id}-card-${index}-text`}
                rows={2}
                maxLength={600}
                value={card.text}
                onChange={(event) => setCard(index, { text: event.target.value })}
                className={`${formControlClass} resize-y`}
              />
            </Field>
          </ListItem>
        ))}
      </div>
      <div>
        <button
          type="button"
          className={btnSecondaryClass}
          disabled={block.cards.length >= MAX_CARDS}
          onClick={() => update({ cards: [...block.cards, { icon: "check", title: "", text: "" }] })}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          {t(`${k}.fields.addCard`)}
        </button>
      </div>
    </>
  );
};

const MediaForm = ({ block, update, disabled, onBusyChange }) => {
  const { t } = useTranslation();
  return (
    <>
      <Field id={`${block.id}-eyebrow`} label={t(`${k}.fields.eyebrow`)} optional info={t(`${k}.fields.eyebrowTip`)}>
        <input
          id={`${block.id}-eyebrow`}
          type="text"
          maxLength={80}
          value={block.eyebrow}
          onChange={(event) => update({ eyebrow: event.target.value })}
          className={formControlClass}
        />
      </Field>
      <TitleField block={block} update={update} label={t(`${k}.fields.title`)} />
      <RichTextField
        id={`${block.id}-body`}
        label={t(`${k}.fields.description`)}
        value={block.body}
        disabled={disabled}
        onChange={(body) => update({ body })}
      />
      <AboutImageField
        image={block.image}
        disabled={disabled}
        onBusyChange={onBusyChange}
        onChange={(image) => update({ image })}
      />
      <RadioRow
        name={`${block.id}-side`}
        label={t(`${k}.fields.imageSide`)}
        value={block.side}
        onChange={(side) => update({ side })}
        options={[
          { value: "left", label: t(`${k}.fields.imageLeft`) },
          { value: "right", label: t(`${k}.fields.imageRight`) },
        ]}
      />
    </>
  );
};

MediaForm.propTypes = { ...formPropTypes, onBusyChange: PropTypes.func };

const FaqForm = ({ block, update }) => {
  const { t } = useTranslation();
  const setItem = (index, patch) =>
    update({ items: block.items.map((item, i) => (i === index ? { ...item, ...patch } : item)) });

  return (
    <>
      <TitleField block={block} update={update} label={t(`${k}.fields.sectionTitle`)} />
      <div className="flex flex-col gap-3">
        {block.items.map((item, index) => (
          <ListItem
            key={index}
            title={t(`${k}.fields.question`, { number: index + 1 })}
            index={index}
            count={block.items.length}
            removeLabel={t(`${k}.fields.removeQuestion`)}
            onMove={(step) => update({ items: moveItem(block.items, index, step) })}
            onRemove={() => update({ items: block.items.filter((_, i) => i !== index) })}
          >
            <Field id={`${block.id}-q-${index}`} label={t(`${k}.fields.questionText`)}>
              <input
                id={`${block.id}-q-${index}`}
                type="text"
                maxLength={200}
                value={item.question}
                onChange={(event) => setItem(index, { question: event.target.value })}
                className={formControlClass}
              />
            </Field>
            <Field id={`${block.id}-a-${index}`} label={t(`${k}.fields.answer`)}>
              <textarea
                id={`${block.id}-a-${index}`}
                rows={3}
                maxLength={2000}
                value={item.answer}
                onChange={(event) => setItem(index, { answer: event.target.value })}
                className={`${formControlClass} resize-y`}
              />
            </Field>
          </ListItem>
        ))}
      </div>
      <div>
        <button
          type="button"
          className={btnSecondaryClass}
          disabled={block.items.length >= MAX_QUESTIONS}
          onClick={() => update({ items: [...block.items, { question: "", answer: "" }] })}
        >
          <i className="bi bi-plus-lg" aria-hidden="true" />
          {t(`${k}.fields.addQuestion`)}
        </button>
      </div>
    </>
  );
};

const LinkForm = ({ block, update }) => {
  const { t } = useTranslation();
  const href = block.href.trim();
  const invalid = href !== "" && !safeAboutUrl(href);

  return (
    <>
      <Field id={`${block.id}-text`} label={t(`${k}.fields.buttonText`)}>
        <input
          id={`${block.id}-text`}
          type="text"
          maxLength={80}
          value={block.text}
          onChange={(event) => update({ text: event.target.value })}
          className={formControlClass}
        />
      </Field>
      <Field
        id={`${block.id}-href`}
        label={t(`${k}.fields.href`)}
        hint={invalid ? undefined : href ? t(`${k}.fields.hrefNewTab`) : t(`${k}.fields.hrefMissing`)}
      >
        <input
          id={`${block.id}-href`}
          type="url"
          inputMode="url"
          placeholder="https://"
          maxLength={500}
          value={block.href}
          aria-invalid={invalid}
          onChange={(event) => update({ href: event.target.value })}
          className={formControlClass}
        />
        {invalid && (
          <p className="mt-1.5 mb-0 text-xs text-red-600" role="alert">
            {t(`${k}.invalidUrl`)}
          </p>
        )}
      </Field>
      <RadioRow
        name={`${block.id}-style`}
        label={t(`${k}.fields.buttonStyle`)}
        value={block.style}
        onChange={(style) => update({ style })}
        options={[
          { value: "solid", label: t(`${k}.fields.styleSolid`) },
          { value: "outline", label: t(`${k}.fields.styleOutline`) },
        ]}
      />
      <RadioRow
        name={`${block.id}-align`}
        label={t(`${k}.fields.align`)}
        value={block.align}
        onChange={(align) => update({ align })}
        options={[
          { value: "left", label: t(`${k}.fields.alignLeft`) },
          { value: "center", label: t(`${k}.fields.alignCenter`) },
        ]}
      />
    </>
  );
};

const BookingForm = ({ block, update }) => {
  const { t } = useTranslation();
  return (
    <>
      <TitleField block={block} update={update} label={t(`${k}.fields.title`)} />
      <Field id={`${block.id}-text`} label={t(`${k}.fields.description`)}>
        <textarea
          id={`${block.id}-text`}
          rows={3}
          maxLength={500}
          value={block.text}
          onChange={(event) => update({ text: event.target.value })}
          className={`${formControlClass} resize-y`}
        />
      </Field>
      <PanelNote icon="bi-calendar2-check">{t(`${k}.fields.bookingNote`)}</PanelNote>
    </>
  );
};

const CustomForm = ({ block, onEditHtml }) => {
  const { t } = useTranslation();
  return (
    <>
      <PanelNote icon="bi-code-slash">{t(`${k}.fields.customNote`)}</PanelNote>
      <div className="max-h-72 overflow-auto rounded-xl border border-ink-200 bg-white p-4">
        <AboutContent value={ABOUT_HTML_MARKER + block.html} />
      </div>
      <div>
        <button type="button" className={btnSecondaryClass} onClick={onEditHtml}>
          <i className="bi bi-code-slash" aria-hidden="true" />
          {t(`${k}.fields.editHtml`)}
        </button>
      </div>
    </>
  );
};

CustomForm.propTypes = { block: PropTypes.object.isRequired, onEditHtml: PropTypes.func.isRequired };

[TextForm, CardsForm, FaqForm, LinkForm, BookingForm].forEach((form) => {
  form.propTypes = formPropTypes;
});

const FORMS = { text: TextForm, cards: CardsForm, media: MediaForm, faq: FaqForm, link: LinkForm, booking: BookingForm };

/** The form for whichever kind `block` is. */
const BlockForm = ({ block, update, disabled = false, onBusyChange, onEditHtml }) => {
  if (block.type === "custom") return <CustomForm block={block} onEditHtml={onEditHtml} />;
  const Form = FORMS[block.type];
  return <Form block={block} update={update} disabled={disabled} onBusyChange={onBusyChange} />;
};

BlockForm.propTypes = {
  block: PropTypes.object.isRequired,
  update: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onBusyChange: PropTypes.func,
  onEditHtml: PropTypes.func.isRequired,
};

export default BlockForm;
